<template>
    <div class="progress-container">
        <!-- Desktop Layout -->
        <div ref="wrapper" class="steps-wrapper desktop-only">
            <div 
                v-for="(label, index) in steps" 
                :key="index" 
                class="step-item"
            >
                <span 
                    ref="labels"
                    class="step-label"
                    :class="{ 'active-step-label': isCurrentStep(index) }"
                    :style="{ color: isCurrentStep(index) ? activeStepColor : isReachedStep(index) ? previousStepColor : nextStepColor }"
                >
                    {{ label }}
                </span>
                <div 
                    class="vertical-line"
                    :style="{ backgroundColor: isReachedStep(index) ? verticalRuleActiveColor : verticalRuleColor }"
                />
            </div>
        </div>
        <!-- Mobile Layout -->
        <div class="steps-wrapper mobile-only">
            <div 
                v-for="(label, index) in steps" 
                :key="index" 
                class="step-item"
            >
                <div 
                    class="step-circle"
                    :style="{ 
                        backgroundColor: isReachedStep(index) ? activeStepColor : 'transparent',
                        borderColor: isReachedStep(index) ? activeStepColor : nextStepColor,
                        color: isReachedStep(index) ? '#FFFFFF' : nextStepColor
                    }"
                >
                    {{ index + 1 }}
                </div>
            </div>
        </div>
        <!-- Horizontal Progress Bar -->
        <div 
            class="progress-track"
            :style="{ backgroundColor: trackColor }"
        >
            <div
                class="progress-fill"
                :style="{ 
                    width: fillPercentage + '%', 
                    backgroundColor: progressColor 
                }"
            />
        </div>
        <!-- Mobile Layout -->
        <div class="mobile-summary mobile-only">
            <span :style="{ color: activeStepColor }">
                Step {{ currentStep }}: {{ steps[currentStep - 1] }}
            </span>
        </div>
    </div>
</template>

<script>
export default {
    name: "ProgressBar",
    props: {
        steps: {
            type: Array,
            default: () => ["Find order", "Review cost", "Billing details"]
        },
        currentStep: {
            type: Number,
            default: 1
        },
        activeStepColor: {
            type: String,
            default: "#0691EF"
        },
        previousStepColor: {
            type: String,
            default: "#0691EF"
        },
        nextStepColor: {
            type: String,
            default: "#697586"
        },
        verticalRuleColor: {
            type: String,
            default: "#CDD5DF"
        },
        verticalRuleActiveColor: {
            type: String,
            default: "#0691EF"
        },
        trackColor: {
            type: String,
            default: "#EEF2F6"
        },
        progressColor: {
            type: String,
            default: "#5EB1F3"
        }
        
    },
    data() {
        return {
            fillPercentage: 0,
            isMobile: false
        };
    },
    watch: {
        currentStep() {
            this.updateProgressWidth();
        },
        steps() {
            this.$nextTick(() => {
                this.updateProgressWidth();
            });
        }
    },
    mounted() {
        this.checkMobile();
        this.updateProgressWidth();
        window.addEventListener("resize", this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
    },
    methods: {
        isCurrentStep(index) {
            return index === this.currentStep - 1;
        },
        isReachedStep(index) {
            return index < this.currentStep;
        },
        handleResize() {
            this.checkMobile();
            this.updateProgressWidth();
        },
        checkMobile() {
            this.isMobile = window.innerWidth <= 600;
        },
        updateProgressWidth() {
            this.$nextTick(() => {
                if (this.isMobile) {
                    // Mobile percentage logic based on step count
                    if (!this.steps || this.steps.length <= 1) {
                        this.fillPercentage = 100;
                    } else {
                        const progress = (this.currentStep / this.steps.length) * 100;
                        this.fillPercentage = Math.min(Math.max(progress, 0), 100);
                    }
                    return;
                }

                // Desktop percentage calculation (right edge of target label)
                const wrapper = this.$refs.wrapper;
                const labels = this.$refs.labels;
                const activeIndex = this.currentStep - 1;

                if (!wrapper || !labels || !labels[activeIndex]) return;

                const wrapperRect = wrapper.getBoundingClientRect();
                const targetLabelRect = labels[activeIndex].getBoundingClientRect();

                const rightEdgeOffset = targetLabelRect.right - wrapperRect.left;
                const calculatedPercent = (rightEdgeOffset / wrapperRect.width) * 100;

                this.fillPercentage = Math.min(Math.max(calculatedPercent, 0), 100);
            });
        }
    }
};
</script>

<style scoped>
.progress-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    margin-top: 24px;
    margin-bottom: 47px;
}

.steps-wrapper {
    display: flex;
    justify-content: space-between;
    width: 100%;
}

.step-item {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.step-label {
    font-family: var(--font-family-Inter, Inter);
    font-size: var(--font-size-hs, 20px);
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    letter-spacing: -0.2px;
    text-align: center;
    transition: color 0.3s ease;
}

.active-step-label {
    font-weight: 600;
}

.vertical-line {
    width: 4px;
    height: 10px;
    border-radius: 12px;
    transition: background-color 0.3s ease;
}

.progress-track {
    width: 100%;
    height: 12px;
    border-radius: 12px;
    overflow: hidden;
    transition: background-color 0.3s ease;
    margin-top: 16px;
}

.progress-fill {
    height: 100%;
    border-radius: 12px;
    background-color: #5EB1F3;
    transition: width 0.3s ease, background-color 0.3s ease;
}

/* Mobile Stepper Circles */
.step-circle {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    transition: all 0.3s ease;
    margin-bottom: 8px;
    line-height: 1;
    box-sizing: border-box;
    padding: 0;
    text-align: center;
}

.mobile-summary {
    margin-top: 12px;
    text-align: center;
    font-weight: 600;
    font-size: 14px;
}

/* Responsive Display Visibility */
.mobile-only {
    display: none;
}

@media (max-width: 600px) {
    .desktop-only {
        display: none !important;
    }
    .mobile-only {
        display: flex;
    }
    .mobile-summary.mobile-only {
        display: block;
    }
}
</style>
